import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, BarChart3, Building2, Briefcase } from "lucide-react";
import { cn } from "@/lib/utils";

const PERSONAS = [
  {
    id: "cfo",
    icon: BarChart3,
    label: "DAF / CFO de PME",
    range: "10 à 250 salariés",
    headline: "Clôturez plus vite, décidez mieux.",
    pain: "Vous perdez 12h/semaine à réconcilier des extraits bancaires, courir après les factures en retard et compiler des exports Excel qui datent déjà d'hier.",
    kpis: [
      { v: "J+3", l: "Clôture mensuelle" },
      { v: "-12h", l: "Réconciliation / sem." },
      { v: "×3", l: "Visibilité cash 90j" },
    ],
    proof: [
      "Rapprochement bancaire automatique DSP2 (Bridge, Powens)",
      "Prévisionnel de trésorerie glissant 90 jours et simulations de runway",
      "Multi-entités, consolidation par groupe, matrice IFRS-ready",
      "Board Pack export PDF prêt à envoyer au comité de direction",
    ],
    quote: {
      text: "Avant, on clôturait à J+15 dans le stress. Aujourd'hui c'est J+3, sans erreur.",
      author: "Camille R.",
      role: "DAF · Groupe industriel 180 salariés",
    },
  },
  {
    id: "ceo",
    icon: Building2,
    label: "Dirigeant TPE / PME",
    range: "< 50 salariés",
    headline: "Un seul outil pour tout gérer, vraiment.",
    pain: "Factures, devis, notes de frais, contrats, RH, flotte : chaque chose vit dans son coin. Vous passez plus de temps à administrer qu'à vendre.",
    kpis: [
      { v: "-8j", l: "Délai encaissement" },
      { v: "98%", l: "Auto-collecte factures" },
      { v: "0", l: "Ligne Excel manuelle" },
    ],
    proof: [
      "Facturation Factur-X 2026 native · devis · relances automatisées",
      "Cartes de paiement physique et virtuelles + OCR des tickets",
      "Signature électronique intégrée pour vos devis et contrats",
      "Assistant IA qui rédige vos relances impayés en 1 clic",
    ],
    quote: {
      text: "Je gère mes 24 salariés, mes factures et ma trésorerie sur le même écran. Fini les 6 abonnements séparés.",
      author: "Julien M.",
      role: "Fondateur · agence digitale · 24 salariés",
    },
  },
  {
    id: "expert",
    icon: Briefcase,
    label: "Cabinet d'expertise-comptable",
    range: "Multi-dossiers illimités",
    headline: "Passez plus de temps sur le conseil, moins sur la saisie.",
    pain: "Chaque nouveau dossier client, c'est des heures perdues à récupérer des pièces, saisir manuellement et gérer les allers-retours pour valider les écritures.",
    kpis: [
      { v: "×2,5", l: "Productivité collaborateur" },
      { v: "-70%", l: "Saisie manuelle" },
      { v: "24/7", l: "Portail client synchro" },
    ],
    proof: [
      "Multi-dossiers avec accès collaboratif expert ↔ client sécurisé",
      "Import FEC direct · export vers Cegid, Sage, ACD, Isacompta",
      "Écritures automatiques via règles OCR + apprentissage par client",
      "Alertes conformité (échéances TVA, CVAE, DSN) automatisées",
    ],
    quote: {
      text: "On a doublé le nombre de dossiers gérés par collaborateur sans embauche.",
      author: "Marie-Anne D.",
      role: "Expert-comptable associée · cabinet 12 pers.",
    },
  },
];

export default function PersonaSelector() {
  const [active, setActive] = useState("cfo");
  const p = PERSONAS.find((x) => x.id === active);

  return (
    <section
      id="personas"
      className="py-20 lg:py-28 relative"
      data-testid="persona-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#4F46E5]">
            Pour qui ?
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B0F17]">
            Une plateforme, trois usages exigeants.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Chaque persona a son propre plan de pilotage. Basculez ci-dessous pour
            voir votre expérience.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap justify-center gap-2" role="tablist">
          {PERSONAS.map((x) => {
            const Icon = x.icon;
            const on = active === x.id;
            return (
              <button
                key={x.id}
                role="tab"
                aria-selected={on}
                onClick={() => setActive(x.id)}
                data-testid={`persona-tab-${x.id}`}
                className={cn(
                  "group flex items-center gap-2.5 px-5 py-3 rounded-xl border transition-all text-sm font-semibold",
                  on
                    ? "bg-[#0B0F17] text-white border-[#0B0F17] shadow-lg shadow-slate-900/10"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300",
                )}
              >
                <Icon
                  className={cn(
                    "h-4 w-4",
                    on ? "text-indigo-300" : "text-slate-500",
                  )}
                />
                <div className="text-left">
                  <div>{x.label}</div>
                  <div
                    className={cn(
                      "text-[10px] font-medium",
                      on ? "text-slate-300" : "text-slate-400",
                    )}
                  >
                    {x.range}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div
          key={active}
          className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 animate-in fade-in duration-500"
          data-testid={`persona-panel-${active}`}
        >
          {/* Left: headline + proof */}
          <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B0F17]">
              {p.headline}
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{p.pain}</p>

            <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-6 py-5 border-y border-slate-100">
              {p.kpis.map((k) => (
                <div key={k.l} className="text-center">
                  <div
                    className="text-2xl sm:text-3xl font-bold text-[#0B0F17] tabular-nums"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {k.v}
                  </div>
                  <div className="text-[11px] font-medium text-slate-500 mt-1">
                    {k.l}
                  </div>
                </div>
              ))}
            </div>

            <ul className="mt-6 space-y-3">
              {p.proof.map((x) => (
                <li key={x} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-50 grid place-items-center shrink-0">
                    <Check className="h-3 w-3 text-emerald-600" />
                  </span>
                  {x}
                </li>
              ))}
            </ul>

            <Link
              to={`/register?plan=${
                p.id === "cfo" ? "scale" : p.id === "expert" ? "cabinet" : "business"
              }`}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#4F46E5] hover:text-[#4338CA] group"
              data-testid={`persona-cta-${active}`}
            >
              Voir le plan recommandé pour ce profil
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          {/* Right: quote */}
          <div className="lg:col-span-5 rounded-2xl bg-[#0F172A] text-white p-6 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl" />
            <div className="text-5xl leading-none text-indigo-400 mb-4 font-serif">
              &ldquo;
            </div>
            <blockquote className="text-lg sm:text-xl leading-relaxed font-medium">
              {p.quote.text}
            </blockquote>
            <div className="mt-8 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-emerald-400 grid place-items-center text-sm font-bold text-slate-900">
                {p.quote.author
                  .split(" ")
                  .map((s) => s[0])
                  .join("")}
              </div>
              <div>
                <div className="text-sm font-semibold">{p.quote.author}</div>
                <div className="text-xs text-slate-400">{p.quote.role}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
